import { ApiContactPoint, ApiContactPointCategory } from '../api-types/contact-point.api';
import { ApiCreateContactPoint } from '../api-types/relative.api';
import { ContactPoint, ContactPointCategory } from '../app-types/contact-point.app';
import { CreateContactPoint } from '../app-types/relative.app';

/**
 * Transforms an API Contact Point object to an application-specific Contact Point object.
 * @param input The API Contact Point object
 * @returns The transformed application-specific Contact Point object
 */
export const tranformApiContactPointToAppContactPoint = (input: ApiContactPoint) => {
	const output: ContactPoint = {
		id: input.id,
		personId: input.personId!,
		personTypeId: input.personTypeId!,
		preferredWayOfContact: input.preferredWayOfContact,
		categoryContactPointId: input.categoryContactPointId,
		contactValue: input.contactValue,
		remark: input.remark,
	};
	return output;
};

/**
 * Transforms an array of API Contact Points to an array of application-specific Contact Points.
 * @param input The API Contact Points array
 * @returns The transformed application-specific Contact Points array.
 */
export const transformApiContactPointsToAppContactPoints = (input: ApiContactPoint[]) => {
	return input.map(tranformApiContactPointToAppContactPoint);
};

/**
 * Transforms an application-specific Create Contact Point object to an API Create Contact Point object.
 * @param input The Contact Point object to be transformed.
 * @returns The transformed API Contact Point object.
 */
export const transformAppContactPointToApiCreateContactPoint = (input: CreateContactPoint): ApiCreateContactPoint => {
	const output: ApiCreateContactPoint = {
		id: input.id,
		preferredWayOfContact: input.preferredWayOfContact,
		categoryContactPointId: input.categoryContactPointId,
		contactValue: input.contactValue,
		remark: input.remark,
	};
	return output;
};

export const transformAppContactPointToApiContactPoint = (input: ContactPoint): ApiContactPoint => {
	const output: ApiContactPoint = {
		id: input.id!,
		preferredWayOfContact: input.preferredWayOfContact,
		categoryContactPointId: input.categoryContactPointId,
		contactValue: input.contactValue,
		remark: input.remark,
	};
	return output;
};

/**
 * Transforms an array of Contact Points to an array of API Contact Points.
 * @param input The API Contact Points array
 * @returns The transformed API-specific Contact Points array.
 */
export const transformAppContactPointsToApiContactPoints = (input: ContactPoint[]) => {
	return input.map(transformAppContactPointToApiCreateContactPoint);
};

/**
 * Transforms an array of application-specific Contact Points to an array of API Contact Points.
 * @param input The Contact Points array to be transformed.
 * @returns The transformed API Contact Points array.
 */
export const transformAppCreateContactPointsToApiContactPoints = (
	input: CreateContactPoint[],
): ApiCreateContactPoint[] => {
	return input.map(transformAppContactPointToApiCreateContactPoint);
};

/**
 * Transforms an API Contact Point Category object to an application-specific Contact Point Category object.
 * @param input The API Contact Point Category object.
 * @returns The transformed Contact Point Category object.
 */
export const transformApiCategoryContactPointToCategoryContactPoint = (
	input: ApiContactPointCategory,
): ContactPointCategory => {
	return {
		id: input.id,
		protocol: input.protocol,
		remark: input.remark,
		initial: input.initial,
	};
};

/**
 * Transforms an array of API Contact Point Category objects to an array of application-specific Contact Point Category objects.
 * @param input The array of API Contact Point Category objects.
 * @returns The transformed array of Contact Point Category objects.
 */
export const transformApiCategoriesContactPointToCategoryContactPoints = (
	input: ApiContactPointCategory[],
): ContactPointCategory[] => {
	return input.map(transformApiCategoryContactPointToCategoryContactPoint);
};

/**
 * Transforms an application-specific Contact Point object to an API Contact Point object.
 * @param input The Contact Point object to be transformed.
 * @returns The transformed API Contact Point object.
 */
export const transformContactPointToApiContactPoint = (input: ContactPoint): ApiContactPoint => {
	return {
		id: input.id!,
		categoryContactPointId: input.categoryContactPointId,
		contactValue: input.contactValue,
		preferredWayOfContact: input.preferredWayOfContact,
		country: undefined,
		remark: input.remark,
		personId: input.personId,
		personTypeId: input.personTypeId,
	};
};
